import {
  type FeatureBillingTier,
  isFeatureEnabled,
  ProjectFeature,
  type SubscriptionFeatureConfig,
} from '@civilgrid/utils/src/common/featureAccess';
import {
  SITE_DATA_FIELD_NOTES,
  SITE_DATA_LAYER_ID_PREFIX,
  SITE_DATA_POTHOLES,
} from '@civilgrid/utils/src/extract/constants';
import { SITE_DATA_APP_LAYER_SEEDS, SITE_DATA_TOC_PARENT } from '@civilgrid/utils/src/layerConfig/siteDataCatalog';
import type { GeoItem, TOCChild, TOCParent } from '~/common/types';
import {
  computeSiteDataAvailability,
  isSiteDataLeafEmpty,
  SITE_DATA_LAYER_IDS,
  type SiteDataAvailability,
} from '~/store2/siteDataLayers';

/** Same label the map TOC gives the Site Data parent. */
export const SITE_DATA_SECTION_LABEL = SITE_DATA_TOC_PARENT.label;

/** The picker keys this section's expansion by it (see `expansionKey`). */
export const SITE_DATA_SECTION_ID = 'site-data-download';

/** Shown for a `siteData:` entry we no longer recognise (id retired since the download ran). */
export const SITE_DATA_LAYER_FALLBACK_LABEL = 'Site data';

// Catalog labels keyed by seed key, which is also the `siteData:` id suffix.
const labelByKey = new Map(SITE_DATA_APP_LAYER_SEEDS.map((seed) => [seed.key, seed.label]));

/** Human label for a `siteData:` layer id, never raw — unknown suffixes get the generic fallback. */
export function getSiteDataLayerLabel(layerId: string): string {
  return labelByKey.get(layerId.slice(SITE_DATA_LAYER_ID_PREFIX.length)) ?? SITE_DATA_LAYER_FALLBACK_LABEL;
}

// The picker carries the export contract's `siteData:` ids; the map's own Site Data layers
// (map/FieldNotesLayer.tsx, map/PotholesLayer.tsx) render off the bare catalog key.
const mapLayerIdByPickerId = new Map<string, string>([
  [SITE_DATA_FIELD_NOTES, SITE_DATA_LAYER_IDS.fieldNotes],
  [SITE_DATA_POTHOLES, SITE_DATA_LAYER_IDS.potholes],
]);

/**
 * The `layerStore.visibleLayerIds` id a picker leaf drives on the preview map. Site-data leaves
 * translate to their catalog key; every other picker id is already a map id and passes through.
 */
export function toMapPreviewLayerId(layerId: string): string {
  return mapLayerIdByPickerId.get(layerId) ?? layerId;
}

/** Resolved leaf presence, or `undefined` until geo items and the org are loaded. */
export function getSiteDataAvailability(
  geoItems: readonly GeoItem[] | undefined,
  organizationLoaded: boolean,
  mobileReportType: string | undefined,
): SiteDataAvailability | undefined {
  if (!geoItems || !organizationLoaded) return undefined;
  return computeSiteDataAvailability(geoItems, mobileReportType);
}

// TOCParent with children narrowed to leaves.
type SiteDataPickerSection = Omit<TOCParent, 'children'> & { children: TOCChild[] };

/**
 * Extract-tab picker section for the two `siteData:` layers (potholes + field notes). Returns
 * undefined (no group rendered) unless every gate passes:
 *   1. ProjectFeature.FieldNotes check — tier plus the org's `featureAccess` override, the
 *      same pair extract submit enforces
 *   2. `pothole_fieldnote_available_in_cad_export_ui` PostHog flag
 *   3. `site-data-layer-enabled` PostHog flag — the map TOC's kill switch for the section
 *   4. data presence — a leaf with no rows of its own type is left out, and the section is
 *      left out while availability is still loading
 * Leaf order matches the map TOC.
 */
export function buildSiteDataPickerSection({
  billingTier,
  exportEnabled,
  siteDataLayerEnabled,
  subscription,
  availability,
}: {
  billingTier: FeatureBillingTier | undefined;
  exportEnabled: boolean;
  siteDataLayerEnabled: boolean;
  subscription?: SubscriptionFeatureConfig | null;
  availability: SiteDataAvailability | undefined;
}): SiteDataPickerSection | undefined {
  if (
    !exportEnabled ||
    !siteDataLayerEnabled ||
    !availability ||
    !billingTier ||
    !isFeatureEnabled(ProjectFeature.FieldNotes, billingTier, subscription)
  ) {
    return undefined;
  }
  const children = [SITE_DATA_FIELD_NOTES, SITE_DATA_POTHOLES]
    .filter((id) => !isSiteDataLeafEmpty(toMapPreviewLayerId(id), availability))
    .map((id) => ({
      id,
      label: getSiteDataLayerLabel(id),
    }));
  if (children.length === 0) return undefined;
  return { label: SITE_DATA_SECTION_LABEL, expanded: true, sectionId: SITE_DATA_SECTION_ID, children };
}
